import { useMemo, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import { FormHelperText, IconButton, MenuItem, Select, Typography } from "@mui/material";
import { chartColorList } from "../constants";
import {
    Tooltip,
    ResponsiveContainer,
    Legend,
    BarChart,
    XAxis,
    YAxis,
    Bar,
} from "recharts";
import { useAppDispatch } from '../../../hooks/StoreHooks';
import { fetchTotalPointThunk } from "../../../store/thunk/reportingThunk";
import { useTotalPointsData } from "../../../store/selectors/reportingSelectors";
import { useTranslation } from "react-i18next";
import {
    OptionContainer,
    OptionRow,
    ActionRow,
    StyledFormControl,
    ChartContainer,
    NoDataText
} from "./Widget.styles";
import { NoDataContainer } from "../Dashboard.styles";
import { GeneralButton } from "../../../common/ui/GeneralButton";

const takeOptions: number[] = [5, 10, 15, 20];

const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
        return (
            <div style={{
                backgroundColor: "white",
                padding: "10px",
                border: "1px solid #ccc",
                borderRadius: "5px",
                boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)"
            }}>
                <Typography fontWeight="bold">{payload[0].payload.name}</Typography>
                <Typography>{`Points: ${payload[0].value}`}</Typography>
            </div>
        );
    }
    return null;
};

const TotalPointsChart = () => {
    const { t } = useTranslation();
    const totalPointsData = useTotalPointsData();
    const [take, setTake] = useState<number>(5);
    const [loading, setLoading] = useState<boolean>(false);
    const [emptyChartData, setEmptyChartData] = useState<boolean>(true);
    const dispatch = useAppDispatch();

    const handleTake = (e: any) => setTake(e.target.value);

    const fetchChartData = () => {
        setLoading(false);
        dispatch(fetchTotalPointThunk({ take })).then(() => {
            setLoading(true);
            setEmptyChartData(false);
        });
    };

    const resetChartData = () => {
        setEmptyChartData(true);
    };

    const formattedChartData = useMemo(() => (
        emptyChartData ?
            [{ name: "", score: 0, fill: "#cccccc" }] :
            totalPointsData.map((elem, i) => ({
                name: `${elem.firstName} ${elem.lastName}`,
                score: elem.totalPoints,
                fill: chartColorList[i % chartColorList.length]
            }))
    ), [totalPointsData, emptyChartData]);

    return (
        <>
            <OptionContainer>
                <OptionRow>
                    <StyledFormControl>
                        <Select
                            value={take}
                            onChange={handleTake}
                            IconComponent={ExpandMoreIcon}
                        >
                            {takeOptions.map((item) => (
                                <MenuItem key={item} value={item}>{item}</MenuItem>
                            ))}
                        </Select>
                        <FormHelperText>{t("dashboardPage.amountOfUsers")}</FormHelperText>
                    </StyledFormControl>
                    <IconButton
                        onClick={resetChartData}
                        aria-label={t("buttons.reset")}
                        sx={{ marginRight: 1, color: "#555" }}
                        size="small"
                    >
                        <RefreshIcon fontSize="small" />
                    </IconButton>
                </OptionRow>

                <ActionRow>
                    <GeneralButton
                        appVariant="widget"
                        onClick={fetchChartData}
                    >
                        {t("buttons.apply")}
                    </GeneralButton>
                </ActionRow>
            </OptionContainer>

            {loading && formattedChartData.length === 0 ? (
                <NoDataContainer>
                    <NoDataText>{t("dashboardPage.noDataAvailable")}</NoDataText>
                </NoDataContainer>
            ) : (
                <ChartContainer>
                    <ResponsiveContainer>
                        <BarChart
                            data={formattedChartData}
                            margin={{ top: 10, right: 30, left: 0, bottom: 5 }}
                            barCategoryGap="20%"
                        >
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                            <Bar dataKey="score" fill={formattedChartData[0]?.fill || "#82ca9d"} />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartContainer>
            )}
        </>
    );
};

export default TotalPointsChart;
